/**
 * @Author: Alex Sorafumo <Yuion>
 * @Date:   18/11/2016 4:31 PM
 * @Email:  alex@yuion.net
 * @Filename: slider.component.ts
 * @Last modified by:   Alex Sorafumo
 * @Last modified time: 01/02/2017 11:52 AM
 */

import { Component, Input, ViewChild, OnChanges, forwardRef, ElementRef, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { WIDGETS } from '../../../settings';
import { Animate } from '../../../services/animate.service';
import { BaseFormWidgetComponent } from '../../../shared/base-form.component';

@Component({
    selector: 'slider',
    template: `<div [class]="'slider' + (klass ? ' ' + klass : '') + (disabled ? ' disable' : '')" (window:resize)="resize(true)" widget>
    <div #space class="block" [class.vertical]="align==='vertical'">
        <!-- Vertical Slider -->
        <div #bar class="bar c1" (touchrelease)="clickSlider($event)" (press)="clickSlider($event)" (pan)="moveSlider($event)">
            <div #prog class="progress c2"
                [style.width]="align==='vertical' ? '100%' :percent + '%'"
                [style.height]="align==='vertical' ? (percent) + '%':'100%' ">
            </div>
            <div class="knob-container"
                [style.left]="align==='vertical' ? '50%' :percent + '%'"
                [style.top]="align==='vertical' ? (100 - percent) + '%':'50%'">
                <div #knob class="knob c3" (touchrelease)="clickSlider($event)" (press)="clickSlider($event)" (pan)="moveSlider($event)"></div>
            <div>
        </div>
    </div>
</div>
`,
    styles: [`.slider{-webkit-box-sizing:border-box;box-sizing:border-box;height:100%;width:100%}.slider *{-webkit-box-sizing:border-box;box-sizing:border-box;cursor:pointer}.slider.disable{opacity:.2;pointer-events:none}.block{cursor:default;padding:1em 1.5em;-ms-touch-action:pan-y;touch-action:pan-y;width:100%}.bar{background-color:rgba(0,0,0,.54);border-radius:.5em;cursor:pointer;height:1em;min-width:1em;position:relative;-ms-touch-action:pan-y;touch-action:pan-y;width:100%}.knob-container{height:0;position:absolute;top:50%;width:0}.knob{background-color:#f0f0f0;border-radius:100%;-webkit-box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);height:2em;left:0;position:absolute;top:0;-ms-touch-action:pan-y;touch-action:pan-y;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);width:2em}.progress{background-color:#2196f3;border-radius:.5em;height:100%;left:0;position:absolute;top:0;-ms-touch-action:pan-y;touch-action:pan-y}.vertical{height:100%;padding:1.5em 1em;-ms-touch-action:pan-x;touch-action:pan-x;width:auto}.vertical .bar{height:100%;min-height:1em;-ms-touch-action:pan-x;touch-action:pan-x;width:1em}.vertical .knob-container{left:50%;top:auto}.vertical .knob{-ms-touch-action:pan-x;touch-action:pan-x}.vertical .progress{bottom:0;top:auto;-ms-touch-action:pan-x;touch-action:pan-x;width:100%}`],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SliderComponent),
        multi: true
    }]
})
export class SliderComponent extends BaseFormWidgetComponent<number> implements OnChanges, ControlValueAccessor {
    @Input() public align = 'horizontal';
    @Input() public min = 0;
    @Input() public max = 100;
    @Input() public step = 1;

    public available = false;
    public current = 0;
    public position = 0;
    public percent = 0;

    @ViewChild('space') private space: ElementRef<HTMLDivElement>;
    @ViewChild('bar') private bar: ElementRef<HTMLDivElement>;

    private previous: number = null;
    private bb: ClientRect;
    private user_action = false;

    constructor(private a: Animate) {
        super();
    }

    public ngAfterViewInit() {
        this.available = true;
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.min || changes.max || changes.model) {
            this.validate();
        }
        if (!this.step) {
            this.step = 1;
        }
        if (changes.model && !this.user_action && !isNaN(this.model)) {
            if (this.model < this.min) {
                this.model = this.min;
            } else if (this.model > this.max) {
                this.model = this.max;
            }
            this.current = this.model;
            this.updateValue(true);
        }
    }

    public validate() {
        if (this.min > this.max) {
            WIDGETS.log('Slider', 'Min > Max');
            this.min = 0;
            this.max = 100;
        }
        if (isNaN(this.max)) {
            WIDGETS.log('Slider', 'Max NaN');
            this.max = this.min > 100 ? this.min + this.step * 5 : 100;
        }
        if (this.min === undefined || this.min === null) {
            WIDGETS.log('Slider', 'Min Undefined');
            this.min = 0;
        } else if (this.min === undefined || this.min === null) {
            WIDGETS.log('Slider', 'Max Undefined');
            this.max = 100;
        } else if (isNaN(this.min)) {
            WIDGETS.log('Slider', 'Min NaN');
            this.min = this.max < 0 ? this.max - this.step * 5 : 0;
        }
        if (isNaN(this.model)) {
            this.model = this.min;
        }
    }

    /**
     * Update model of the slider
     * @param update Do we need to update the display
     */
    public updateValue(update: boolean = false) {
        if (!this.bar) {
            setTimeout(() => { this.updateValue(update); }, 20);
        } else if (update) {
            this.a.animation(() => {
                return;
            }, () => {
                const range = +this.max - +this.min;
                const percent = (this.current - this.min) / range;
                this.percent = Math.round(percent * 10000) / 100;
            }).animate();
        }
    }

    /**
     * Updates the model and position of the slider based of the event
     * @param event Tap event
     */
    public clickSlider(event: MouseEvent | TouchEvent) {
        if (event) {
            if (event.preventDefault) {
                event.preventDefault();
            }
            if (event.stopPropagation) {
                event.stopPropagation();
            }
        }
        const prev = this.current;
        this.current = this.model = this.calcValue(event);
        this.actionPerformed();
        this.refresh();
    }

    /**
     * Updates the position of the progress and knob of the slider
     * @param event Pan event
     */
    public moveSlider(event: MouseEvent | TouchEvent) {
        if (event) {
            if (event.preventDefault) {
                event.preventDefault();
            }
            if (event.stopPropagation) {
                event.stopPropagation();
            }
        }
        const prev = this.current;
        this.current = this.model = this.calcValue(event);
        this.user_action = true;
        this.refresh();
        this.timeout('stop', () => this.sliderStop(event));
    }

    /**
     * Updates the position of the progress and knob of the slider
     * @param event PanEnd event
     */
    public sliderStop(event: MouseEvent | TouchEvent) {
        if (event) {
            if (event.preventDefault) {
                event.preventDefault();
            }
            if (event.stopPropagation) {
                event.stopPropagation();
            }
        }
        this.actionPerformed();
        this.refresh();
    }

    /**
     * Update slider positioning when the window is resized
     */
    public resize(resized: boolean = false, tries: number = 0) {
        if (resized) {
            if (tries > 10) { return; }
            if (this.bar && this.bar.nativeElement) {
                this.bb = this.bar.nativeElement.getBoundingClientRect();
            } else {
                return this.timeout('resize', () => this.resize(resized, tries), 200 * ++tries);
            }
            this.updateValue(true);
        } else {
            this.timeout('resize', () => this.resize(true));
        }
    }

    public checkStatus(e: MouseEvent | TouchEvent, i: number) {
        if (event) {
            if (event.preventDefault) { event.preventDefault(); }
            if (event.stopPropagation) { event.stopPropagation(); }
        }
        if (i > 3 || !this.space) { return; }
        let visible = false;
        let el = this.space.nativeElement;
        // Check if component is attached to the body of the page
        while (el) {
            if (el.nodeName === 'BODY') {
                visible = true;
                break;
            }
            el = el.parentNode as any;
        }
        if (!visible) {
            this.timeout('status', () => { this.checkStatus(e, i + 1); }, 100);
        } else {
            this.resize();
        }
    }

    /**
     * Emits the model throught the output binding
     */
    private postValue() {
        this.timeout('post', () => {
            if (this.user_action) {
                this.timeout('update', () => {
                    this.modelChange.emit(this.current);
                }, 180); // Delay timer for posting model changes.
            }
        }, 20);
    }

    /**
     * Calculates the new model of the slider using the position of the event
     * @param event Tap/Pan event
     * @return Returns the new model of the slider
     */
    private calcValue(event: any) {
        if (event) {
            if (event.preventDefault) { event.preventDefault(); }
            if (event.stopPropagation) { event.stopPropagation(); }
        } else {
            return this.current;
        }
        const align = this.align ? this.align.toLowerCase() : '';
        const isVertical = align.indexOf('vert') >= 0;
        let pos: number;
        let percent = 0;
        const center: { x: number, y: number } = event.center ? event.center : { x: event.clientX, y: event.clientY };
        if (this.bar) {
            if (!isVertical) {
                pos = center.x - this.getBarOffset().x;
                percent = pos / this.bar.nativeElement.offsetWidth;
            } else {
                pos = center.y - this.getBarOffset().y;
                percent = 1 - (pos / this.bar.nativeElement.offsetHeight);
            }
        }

        // Normalise the range
        const range = +this.max - +this.min;

        // expand the model to an number min...max, and clip
        // it to a multiple of step
        const stepped = Math.round((percent * range) / +this.step) * +this.step;

        // constraint min..X..max
        return Math.min(+this.max, Math.max(+this.min, (stepped + +this.min)));
    }

    private actionPerformed() {
        this.clearTimer('update');
        this.clearTimer('action');
        this.user_action = true;
        this.timeout('action', () => this.user_action = false, 300);

    }

    /**
     * Gets the offset of the slider bar
     * @return
     */
    private getBarOffset() {
        const dim = { x: 0, y: 0 };
        if (!this.bar || !this.bar.nativeElement) {
            return dim;
        }
        if (!this.bb) {
            this.bb = this.bar.nativeElement.getBoundingClientRect();
        }
        const el = this.bb;
        dim.x = el.left;
        dim.y = el.top;
        return dim;
    }

    private refresh(post: boolean = true) {
        this.updateValue(true);
        if (post) {
            this.postValue();
        }
    }
}
